import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { Badge } from "@ui/badge/Badge";
import { Box, HBox, VBox } from "@ui/box/Box";
import { IconCalendar } from "@ui/icons/IconCalendar";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";

type TStateStatus = "incomplete" | "pending" | "in_progress" | "completed" | "error";
export interface IRoadmapStep {
	status: TStateStatus;
	state: number;
	name: string;
	date?: string;
}

export const Roadmap = ({
	steps,
	currentStepIndex,
	selectState,
}: {
	steps: IRoadmapStep[];
	currentStepIndex: number;
	selectState?: (index: number) => void;
}) => {
	useLocale();
	return (
		<VBox sx={{ position: "relative" }}>
			{steps.map((step, index) => {
				const isCompleted = step.status === "completed";
				const isCurrent = step.state === currentStepIndex;
				const isPending = step.status === "pending";
				const eventDate = step.date;

				let circleColor = "#e0e0e0";
				let circleBorder = "2px solid #bdbdbd";
				let textColor = "#9e9e9e";
				let lineColor = "#e0e0e0";

				if (isCompleted) {
					circleColor = "#4caf50";
					circleBorder = "2px solid #4caf50";
					textColor = "#2e7d32";
					lineColor = "#4caf50";
				} else if (isCurrent) {
					circleColor = "#2196f3";
					circleBorder = "2px solid #2196f3";
					textColor = "#1565c0";
				}

				return (
					<HBox
						key={index}
						sx={{
							position: "relative",
							minHeight: "80px",
							cursor: step.status !== "pending" && selectState ? "pointer" : "default",
							backgroundColor: "transparent",
							transition: "background-color 0.3s",
							borderRadius: "14px",
							padding: "10px 15px",
							"&:hover": step.status !== "pending" && selectState ? { backgroundColor: "#e8ecf3" } : {},
						}}
						onClick={() => {
							step.status !== "pending" && selectState && selectState(index);
						}}>
						{/* Vertical line */}
						{index < steps.length - 1 && (
							<Box
								sx={{
									position: "absolute",
									left: "34px",
									top: "40px",
									width: "2px",
									height: "calc(100% - 20px)",
									backgroundColor: isCompleted ? lineColor : "#e0e0e0",
								}}
							/>
						)}

						{/* Step circle */}
						<Box
							sx={{
								width: "40px",
								height: "40px",
								borderRadius: "50%",
								backgroundColor: isCompleted || isCurrent ? circleColor : "#fff",
								border: circleBorder,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
								zIndex: 1,
								boxShadow: isCurrent ? "0 0 0 4px rgba(33, 150, 243, 0.2)" : "none",
							}}>
							<Typo
								variant="medium"
								bold
								sx={{
									color: isCompleted || isCurrent ? "#fff" : "#9e9e9e",
								}}>
								{isCompleted ? "✓" : index + 1}
							</Typo>
						</Box>

						<Spacer size="20px" />

						{/* Step content */}
						<VBox sx={{ flexGrow: 1, paddingTop: "10px" }}>
							<HBox sx={{ alignItems: "center", gap: "10px" }}>
								<Typo
									variant="medium"
									bold
									sx={{
										color: textColor,
									}}>
									{step.name}
								</Typo>
								{isCurrent && !isCompleted && <Badge color={"#e3f2fd"} variant="standard" label={msg("inProgress")} />}
								{isCompleted && <Badge color={"#e8f5e9"} variant="standard" label={msg("completed")} />}
							</HBox>
							{eventDate && (
								<HBox sx={{ alignItems: "center", gap: "5px", marginTop: "5px" }}>
									<IconCalendar width={14} height={14} />
									<Typo variant="small" sx={{ color: "#757575" }}>
										{eventDate}
									</Typo>
								</HBox>
							)}
							{isPending && (
								<Typo variant="small" sx={{ color: "#bdbdbd", marginTop: "5px" }}>{msg("pending")}</Typo>
							)}
						</VBox>
					</HBox>
				);
			})}
		</VBox>
	);
};
